/**
 * User identification for analytics.
 *
 * Identity model:
 *  - distinct_id = user.id — stable per human, survives affiliate switching
 *    (one login can own multiple affiliates via the affiliate-select flow).
 *  - agent_code is a SUPER PROPERTY (posthog.register): stamped on every event
 *    at capture time, so each event permanently records which agent context it
 *    happened under — filter/break down anything by agent_code.
 *  - fullname/email/role are person properties for a human-readable People tab.
 *
 * Test accounts (`user.is_test`) are opted out of ALL capture.
 */
import posthog from 'posthog-js';

type AnyRecord = Record<string, unknown>;

function getRoleName(user: AnyRecord | null): string {
  const roles = (user?.roles as string[] | undefined) ?? [];
  if (roles.includes('admin')) return 'Admin';
  if (roles.includes('affiliate')) return 'Affiliate';
  return '';
}

export function identifyAnalyticsUser(affiliate: AnyRecord, user: AnyRecord | null): void {
  const affUser = affiliate.user as AnyRecord | undefined;
  const isTest = Boolean(affUser?.is_test);

  if (isTest) {
    posthog.opt_out_capturing();
    return;
  }
  if (posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing();
  }

  const id = affUser?.id;
  if (id === undefined || id === null) return;

  posthog.identify(String(id), {
    agent_code: affiliate.agent_code,
    role_name: getRoleName(user),
    name: (user?.fullname as string | undefined) ?? affUser?.fullname,
    email: (user?.email as string | undefined) ?? affUser?.email,
    is_oic_verified: affiliate.is_oic_data_verified,
  });

  // Current affiliate context on EVERY event (point-in-time, survives switching).
  posthog.register({
    agent_code: affiliate.agent_code,
    role_name: getRoleName(user),
  });
}
